import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { DoorOpen, Check, X, Loader2, Users, ChevronDown } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const POLL_INTERVAL = 5000;

function timeAgo(isoStr) {
  const diff = Math.floor((Date.now() - new Date(isoStr).getTime()) / 1000);
  if (diff < 60) return 'just now';
  const mins = Math.floor(diff / 60);
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m ago`;
}

export default function WaitingRoomPanel({ meetingId, isHost }) {
  const [waiting, setWaiting] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [loadingIds, setLoadingIds] = useState(new Set());
  const [admittingAll, setAdmittingAll] = useState(false);
  const intervalRef = useRef(null);

  const fetchWaiting = useCallback(async () => {
    if (!meetingId || !isHost) return;
    try {
      const data = await api.get(`/meetings/${meetingId}/waiting`);
      setWaiting(data.waiting || []);
    } catch {
      // Silent
    }
  }, [meetingId, isHost]);

  useEffect(() => {
    if (!isHost) return;
    fetchWaiting();
    intervalRef.current = setInterval(fetchWaiting, POLL_INTERVAL);
    return () => clearInterval(intervalRef.current);
  }, [isHost, fetchWaiting]);

  const handleAdmit = async (participantId) => {
    setLoadingIds((s) => new Set(s).add(participantId));
    try {
      await api.post(`/meetings/${meetingId}/admit/${participantId}`);
      setWaiting((prev) => prev.filter((w) => w.id !== participantId));
      toast.success('Participant admitted');
    } catch {
      toast.error('Failed to admit participant');
    } finally {
      setLoadingIds((s) => { const n = new Set(s); n.delete(participantId); return n; });
    }
  };

  const handleReject = async (participantId) => {
    setLoadingIds((s) => new Set(s).add(participantId));
    try {
      await api.post(`/meetings/${meetingId}/reject/${participantId}`);
      setWaiting((prev) => prev.filter((w) => w.id !== participantId));
      toast.success('Participant declined');
    } catch {
      toast.error('Failed to decline participant');
    } finally {
      setLoadingIds((s) => { const n = new Set(s); n.delete(participantId); return n; });
    }
  };

  const handleAdmitAll = async () => {
    setAdmittingAll(true);
    try {
      const data = await api.post(`/meetings/${meetingId}/admit-all`);
      setWaiting([]);
      toast.success(`Admitted ${data.admitted || 'all'} participant(s)`);
    } catch {
      toast.error('Failed to admit all');
    } finally {
      setAdmittingAll(false);
    }
  };

  if (!isHost) return null;

  const count = waiting.length;

  // Collapsed badge — only show when there are waiting participants
  if (!expanded) {
    if (count === 0) return null;
    return (
      <button
        onClick={() => setExpanded(true)}
        className="relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-background border shadow-sm hover:bg-muted transition-colors text-sm font-medium"
      >
        <DoorOpen className="h-4 w-4" />
        <span>{count}</span>
        <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-red-500 animate-pulse" />
      </button>
    );
  }

  // Expanded panel
  return (
    <div className="relative">
      <button
        onClick={() => setExpanded(false)}
        className="relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-background border shadow-sm hover:bg-muted transition-colors text-sm font-medium"
      >
        <DoorOpen className="h-4 w-4" />
        <span>Waiting Room</span>
        {count > 0 && (
          <Badge variant="destructive" className="h-5 min-w-[20px] px-1.5 text-xs">
            {count}
          </Badge>
        )}
        <ChevronDown className="h-3 w-3" />
      </button>

      {/* Dropdown panel */}
      <div className="absolute top-full right-0 mt-2 w-80 bg-background border rounded-lg shadow-lg z-50 overflow-hidden">
        <div className="px-3 py-2 border-b bg-muted/30 flex items-center justify-between">
          <span className="text-sm font-medium flex items-center gap-1.5">
            <Users className="h-4 w-4" />
            {count} waiting
          </span>
          {count > 1 && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={handleAdmitAll}
              disabled={admittingAll}
            >
              {admittingAll ? (
                <Loader2 className="h-3 w-3 animate-spin mr-1" />
              ) : (
                <Check className="h-3 w-3 mr-1" />
              )}
              Admit All
            </Button>
          )}
        </div>

        <div className="max-h-60 overflow-y-auto divide-y">
          {count === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">
              No one is waiting
            </div>
          ) : (
            waiting.map((p) => {
              const isLoading = loadingIds.has(p.id);
              return (
                <div key={p.id} className="px-3 py-2.5 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-medium truncate">{p.name}</span>
                      {p.is_guest && (
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
                          Guest
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {timeAgo(p.requested_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-green-600 hover:text-green-700 hover:bg-green-50"
                      onClick={() => handleAdmit(p.id)}
                      disabled={isLoading}
                      title="Admit"
                    >
                      {isLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive hover:bg-red-50"
                      onClick={() => handleReject(p.id)}
                      disabled={isLoading}
                      title="Decline"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
